import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import API from "../../../services/API";

const fetchFeePayers = (configId, page = 1, limit = 50) =>
  API.get(`/paymentFee/school/payment-config/${configId}/payers`, {
    headers: { token: localStorage.getItem("token") },
    params: { page, limit },
  });

const FeeConfigPayers = () => {
  const { configId } = useParams();
  const navigate = useNavigate();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);

  const [statusFilter, setStatusFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");

  const loadPayers = async (p = 1) => {
    setLoading(true);

    try {
      const res = await fetchFeePayers(configId, p);

      if (res.data.response_code === 1000) {
        const d = res.data.data || {};

        setRows(d.rows || []);
        setPage(d.page || p);
        setPages(d.pages || 1);
      } else {
        toast.error(res.data.message || "Failed to load payers");
      }
    } catch {
      toast.error("Failed to load payers");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPayers(1);
  }, [configId]);

  const filteredRows = useMemo(() => {
    return rows.filter((p) => {
const isUnpaid = Number(p.expected_status) === 0;
const isPaid = !isUnpaid;

const matchesStatus =
  statusFilter === "all" ||
  (statusFilter === "paid" && isPaid) ||
  (statusFilter === "unpaid" && isUnpaid);

      const keyword = searchTerm.toLowerCase().trim();

      const matchesSearch =
        !keyword ||
        String(p.student_name || "").toLowerCase().includes(keyword) ||
        String(p.parent_name || "").toLowerCase().includes(keyword) ||
        String(p.student_uuid || "").toLowerCase().includes(keyword);

      return matchesStatus && matchesSearch;
    });
  }, [rows, statusFilter, searchTerm]);

  const paidCount = useMemo(
    () => rows.filter((p) => Number(p.expected_status) !== 0).length,
    [rows]
  );

const unpaidCount = useMemo(
  () => rows.filter((p) => Number(p.expected_status) === 0).length,
  [rows]
);

  const totalCollected = useMemo(() => {
    return rows.reduce((sum, p) => {
      if (Number(p.expected_status) === 0) return sum;
      return sum + Number(p.amount || 0);
    }, 0);
  }, [rows]);

  const formatAmount = (amount) => {
    if (!amount) return "—";
    return `RM ${Number(amount).toFixed(2)}`;
  };

  const getInitials = (name) => {
    if (!name) return "NA";

    return name
      .split(" ")
      .map((word) => word[0])
      .join("")
      .slice(0, 2)
      .toUpperCase();
  };

  return (
    <div
      className="container-fluid py-4"
      style={{
        background: "#f8fafc",
        minHeight: "100vh",
      }}
    >
      {/* Header */}
      <div
        className="rounded-5 p-4 p-md-5 mb-4 position-relative overflow-hidden"
        style={{
          background:
            "linear-gradient(135deg, #7f1d1d 0%, #dc2626 48%, #fb7185 100%)",
          boxShadow: "0 24px 60px rgba(220, 38, 38, 0.18)",
        }}
      >
        <div
          className="position-absolute top-0 end-0 rounded-circle"
          style={{
            width: 260,
            height: 260,
            background: "rgba(255,255,255,0.12)",
            transform: "translate(30%, -35%)",
          }}
        />

        <div
          className="position-absolute bottom-0 start-50 rounded-circle"
          style={{
            width: 180,
            height: 180,
            background: "rgba(255,255,255,0.08)",
            transform: "translate(-50%, 45%)",
          }}
        />

        <div className="position-relative d-flex flex-column flex-lg-row justify-content-between gap-4">
          <div>
            <div
              className="d-inline-flex align-items-center gap-2 px-3 py-2 rounded-pill mb-3"
              style={{
                background: "rgba(255,255,255,0.16)",
                color: "#fff",
                border: "1px solid rgba(255,255,255,0.24)",
                backdropFilter: "blur(10px)",
                fontSize: 13,
                fontWeight: 700,
              }}
            >
              <span
                className="rounded-circle"
                style={{
                  width: 8,
                  height: 8,
                  background: "#fff",
                  display: "inline-block",
                }}
              />
              Payment Collection
            </div>

            <h1
              className="text-white fw-black mb-2"
              style={{
                fontSize: "clamp(32px, 4vw, 52px)",
                letterSpacing: "-1.5px",
              }}
            >
              Fee Payers
            </h1>

            <p
              className="text-white-50 mb-0"
              style={{
                maxWidth: 720,
                fontSize: 16,
              }}
            >
              View assigned payers, payment status, received amounts, and
              uploaded receipts for this fee configuration.
            </p>
          </div>

          <div className="d-flex align-items-start gap-2">
            <button
              type="button"
              className="btn border-0 rounded-4 fw-bold px-4 py-3"
              style={{
                background: "rgba(255,255,255,0.16)",
                color: "#fff",
                border: "1px solid rgba(255,255,255,0.24)",
              }}
              onClick={() => navigate(-1)}
            >
              Back
            </button>

            <button
              type="button"
              className="btn border-0 rounded-4 fw-bold px-4 py-3"
              style={{
                background: "#ffffff",
                color: "#991b1b",
                boxShadow: "0 18px 40px rgba(0,0,0,0.12)",
              }}
              onClick={() => loadPayers(page)}
              disabled={loading}
            >
              {loading ? "Refreshing..." : "Refresh"}
            </button>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="row g-3 mb-4">
        <div className="col-md-3">
          <div className="card border-0 rounded-5 shadow-sm h-100">
            <div className="card-body p-4">
              <div className="text-muted small fw-semibold mb-2">
                Total Payers
              </div>
              <div className="display-6 fw-black text-dark">{rows.length}</div>
              <div className="text-muted small mt-2">
                Records on current page
              </div>
            </div>
          </div>
        </div>

        <div className="col-md-3">
          <div className="card border-0 rounded-5 shadow-sm h-100">
            <div className="card-body p-4">
              <div className="text-muted small fw-semibold mb-2">Paid</div>
              <div className="display-6 fw-black text-success">
                {paidCount}
              </div>
              <div className="text-muted small mt-2">Completed payments</div>
            </div>
          </div>
        </div>

        <div className="col-md-3">
          <div className="card border-0 rounded-5 shadow-sm h-100">
            <div className="card-body p-4">
<div className="text-muted small fw-semibold mb-2">Unpaid</div>
<div className="display-6 fw-black text-warning">
  {unpaidCount}
</div>
<div className="text-muted small mt-2">Awaiting payment</div>
            </div>
          </div>
        </div>

        <div className="col-md-3">
          <div className="card border-0 rounded-5 shadow-sm h-100">
            <div className="card-body p-4">
              <div className="text-muted small fw-semibold mb-2">
                Collected
              </div>
              <div className="fw-black text-danger" style={{ fontSize: 30 }}>
                RM {totalCollected.toFixed(2)}
              </div>
              <div className="text-muted small mt-2">
                Paid amount on current page
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Filter Selection */}
      <div className="card border-0 shadow-sm rounded-5 mb-4">
        <div className="card-body p-4">
          <div className="d-flex flex-column flex-lg-row justify-content-between align-items-lg-center gap-3">
            <div>
              <h6 className="fw-black text-dark mb-1">Filter Payers</h6>
              <div className="text-muted small">
                Narrow down payers by payment status or search by name.
              </div>
            </div>

            <div className="d-flex flex-column flex-md-row gap-2">
              <div
                className="btn-group rounded-4 overflow-hidden"
                role="group"
                style={{
                  border: "1px solid #e2e8f0",
                }}
              >
                <button
                  type="button"
                  className={`btn btn-sm fw-semibold px-4 ${
                    statusFilter === "all" ? "btn-danger" : "btn-light"
                  }`}
                  onClick={() => setStatusFilter("all")}
                >
                  All
                </button>

                <button
                  type="button"
                  className={`btn btn-sm fw-semibold px-4 ${
                    statusFilter === "paid" ? "btn-success" : "btn-light"
                  }`}
                  onClick={() => setStatusFilter("paid")}
                >
                  Paid
                </button>

                <button
                  type="button"
                  className={`btn btn-sm fw-semibold px-4 ${
                    statusFilter === "pending" ? "btn-warning" : "btn-light"
                  }`}
                  onClick={() => setStatusFilter("pending")}
                >
                  Pending
                </button>
              </div>

              <input
                className="form-control rounded-4"
                style={{ minWidth: 260 }}
                placeholder="Search student or parent..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />

              {(statusFilter !== "all" || searchTerm) && (
                <button
                  type="button"
                  className="btn btn-light rounded-4 fw-semibold px-4"
                  onClick={() => {
                    setStatusFilter("all");
                    setSearchTerm("");
                  }}
                >
                  Clear
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="card border-0 shadow-sm rounded-5 overflow-hidden">
        <div className="card-header bg-white border-0 p-4">
          <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3">
            <div>
              <h5 className="fw-black text-dark mb-1">Assigned Payers</h5>
              <div className="text-muted small">
                Showing {filteredRows.length} of {rows.length} records for
                configuration #{configId}
              </div>
            </div>

            <div
              className="rounded-pill px-3 py-2 small fw-semibold"
              style={{
                background: "#f1f5f9",
                color: "#334155",
              }}
            >
              Page {page} of {pages}
            </div>
          </div>
        </div>

        <div className="table-responsive">
          <table className="table align-middle mb-0" style={{ minWidth: 880 }}>
            <thead>
              <tr style={{ background: "#f8fafc" }}>
                <th className="border-0 text-muted small fw-bold px-4 py-3">
                  Student
                </th>
                <th className="border-0 text-muted small fw-bold px-4 py-3">
                  Parent
                </th>
                <th className="border-0 text-muted small fw-bold px-4 py-3">
                  Status
                </th>
                <th className="border-0 text-muted small fw-bold px-4 py-3">
                  Amount
                </th>
                <th className="border-0 text-muted small fw-bold px-4 py-3 text-end">
                  Receipt
                </th>
              </tr>
            </thead>

            <tbody>
              {loading && rows.length === 0 && (
                <tr>
                  <td colSpan="5" className="text-center py-5">
                    <div
                      className="spinner-border text-danger mb-3"
                      role="status"
                    />
                    <div className="fw-semibold text-dark">
                      Loading fee payers
                    </div>
                    <div className="text-muted small">
                      Please wait while we fetch the payer records.
                    </div>
                  </td>
                </tr>
              )}

              {!loading &&
                filteredRows.map((p) => {
                  const isPending = Number(p.expected_status) === 0;

                  return (
                    <tr
                      key={p.student_uuid}
                      style={{
                        borderTop: "1px solid #eef2f7",
                      }}
                    >
                      <td className="px-4 py-4">
                        <div className="d-flex align-items-center gap-3">
                          <div
                            className="rounded-4 d-flex align-items-center justify-content-center fw-bold"
                            style={{
                              width: 46,
                              height: 46,
                              background: "#fee2e2",
                              color: "#991b1b",
                              flex: "0 0 auto",
                            }}
                          >
                            {getInitials(p.student_name)}
                          </div>

                          <div>
                            <div className="fw-bold text-dark">
                              {p.student_name || "Unnamed Student"}
                            </div>
                            <div className="text-muted small">
                              {p.student_uuid || "-"}
                            </div>
                          </div>
                        </div>
                      </td>

                      <td className="px-4 py-4">
                        <div className="fw-semibold text-dark">
                          {p.parent_name || "—"}
                        </div>
                        <div className="text-muted small">Linked guardian</div>
                      </td>

                      <td className="px-4 py-4">
                        {isPending ? (
                          <span className="badge rounded-pill bg-warning-subtle text-warning-emphasis px-3 py-2">
                            Pending
                          </span>
                        ) : (
                          <span className="badge rounded-pill bg-success-subtle text-success px-3 py-2">
                            Paid
                          </span>
                        )}
                      </td>

                      <td className="px-4 py-4">
                        <div className="fw-bold text-dark">
                          {formatAmount(p.amount)}
                        </div>
                        <div className="text-muted small">
                          {isPending ? "Not collected yet" : "Received"}
                        </div>
                      </td>

                      <td className="px-4 py-4 text-end">
                        {p.receipt_url ? (
                          <a
                            href={p.receipt_url}
                            target="_blank"
                            rel="noreferrer"
                            className="btn btn-sm btn-light rounded-4 fw-semibold px-3"
                          >
                            View Receipt
                          </a>
                        ) : (
                          <span className="text-muted small">No receipt</span>
                        )}
                      </td>
                    </tr>
                  );
                })}

              {!loading && filteredRows.length === 0 && (
                <tr>
                  <td colSpan="5" className="text-center py-5">
                    <div
                      className="mx-auto mb-3 rounded-circle d-flex align-items-center justify-content-center"
                      style={{
                        width: 70,
                        height: 70,
                        background: "#fee2e2",
                        color: "#dc2626",
                        fontWeight: 900,
                        fontSize: 24,
                      }}
                    >
                      RM
                    </div>

                    <div className="fw-bold text-dark mb-1">
                      No matching payer records
                    </div>

                    <div className="text-muted small">
                      Try changing the filter selection or search keyword.
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div
          className="d-flex flex-column flex-md-row justify-content-between align-items-center gap-3 p-4 bg-white"
          style={{
            borderTop: "1px solid #eef2f7",
          }}
        >
          <div className="text-muted small">
            Showing page <strong>{page}</strong> of <strong>{pages}</strong>
          </div>

          <div className="d-flex align-items-center gap-2">
            <button
              className="btn btn-sm btn-light rounded-4 fw-semibold px-4"
              disabled={page <= 1 || loading}
              onClick={() => loadPayers(page - 1)}
            >
              Prev
            </button>

            <div
              className="rounded-4 px-3 py-2 small fw-bold"
              style={{
                background: "#f8fafc",
                color: "#334155",
                minWidth: 90,
                textAlign: "center",
              }}
            >
              {page} / {pages}
            </div>

            <button
              className="btn btn-sm btn-danger rounded-4 fw-semibold px-4"
              disabled={page >= pages || loading}
              onClick={() => loadPayers(page + 1)}
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FeeConfigPayers;